import { innerSearch } from 'api/Api';
import PayverseHeader from 'app/components/PayverseHeader';
import { usePaymentState } from 'payment_state/PaymentStateContext';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';

const WechatResult = () => {
  const searchParams = useLocation().search;
  const navigate = useNavigate();
  const { setPaymentState } = usePaymentState();
  const [isPolling, setIsPolling] = useState<boolean>(false);
  const paramTid = new URLSearchParams(searchParams).get('tid');
  const { t } = useTranslation();

  // polling / timeout 관리용 ref
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const showButtonTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const pollingEndTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const isPollingStartedRef = useRef(false);
  const isRedirectingRef = useRef(false);

  const clearPollingTimers = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    if (showButtonTimeoutRef.current) {
      clearTimeout(showButtonTimeoutRef.current);
      showButtonTimeoutRef.current = null;
    }

    if (pollingEndTimeoutRef.current) {
      clearTimeout(pollingEndTimeoutRef.current);
      pollingEndTimeoutRef.current = null;
    }

    isPollingStartedRef.current = false;
  }, []);

  const createFormAndSubmit = useCallback(
    (formData: Record<string, unknown>, returnUrl: string) => {
      clearStorage();
      // 중복 submit 방지
      if (isRedirectingRef.current) return;
      isRedirectingRef.current = true;

      // redirect 직전 timer 정리
      clearPollingTimers();

      const form = document.createElement('form');
      form.setAttribute('method', 'POST');
      form.setAttribute('action', returnUrl);
      form.style.display = 'none';

      Object.entries(formData).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.setAttribute('type', 'hidden');
        input.setAttribute('name', key);
        input.setAttribute('value', String(value ?? ''));
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    },
    [clearPollingTimers],
  );

  const startPolling = useCallback(() => {
    // 이미 polling 중이면 중복 실행 방지
    if (isPollingStartedRef.current) return;

    isPollingStartedRef.current = true;

    // 폴링 갭
    pollingIntervalRef.current = setInterval(() => {
      transactPolling.mutate();
    }, 2000);

    // 5초 뒤 close 버튼 노출
    showButtonTimeoutRef.current = setTimeout(() => {
      setIsPolling(true);
    }, 5000);

    // 20초간 폴링 후 마지막 결과 조회
    pollingEndTimeoutRef.current = setTimeout(() => {
      clearPollingTimers();
      transactLastResult.mutate();
    }, 20000);
  }, [clearPollingTimers]);

  const transactResult = useMutation(() => innerSearch(paramTid), {
    onSuccess: data => {
      const returnUrl = data.data.returnUrl;
      const response = data.data.response;

      setPaymentState(prevState => ({
        ...prevState,
        resultMessage: response.resultMessage,
        resultCode: response.resultCode,
        tid: response.tid,
        orderId: response.orderId,
        returnUrl,
        descriptor: data.data.display.descriptor,
      }));

      if (response.resultStatus === 'SUCCESS') {
        createFormAndSubmit(response, returnUrl);
      } else {
        startPolling();
      }
    },
    onError: () => {
      clearPollingTimers();
      navigate('/error');
    },
  });

  const transactPolling = useMutation(() => innerSearch(paramTid), {
    onSuccess: data => {
      const returnUrl = data.data.returnUrl;
      const response = data.data.response;
      const resultStatus = response?.resultStatus;

      if (!resultStatus) {
        clearPollingTimers();
        navigate('/error');
        return;
      }

      if (resultStatus === 'SUCCESS') {
        createFormAndSubmit(response, returnUrl);
      }
    },
    onError: () => {
      clearPollingTimers();
      navigate('/error');
    },
  });

  const transactLastResult = useMutation(() => innerSearch(paramTid), {
    onSuccess: data => {
      const returnUrl = data.data.returnUrl;
      const response = data.data.response;
      const resultStatus = response?.resultStatus;

      if (!resultStatus) {
        navigate('/error');
        return;
      }

      createFormAndSubmit(response, returnUrl);
    },
    onError: () => navigate('/error'),
  });

  const clearStorage = () => {
    window.sessionStorage.removeItem('wechatSubmit');
  };

  useEffect(() => {
    if (paramTid) {
      transactResult.mutate();
    }

    return () => {
      clearPollingTimers();
    };
  }, [paramTid, clearPollingTimers]);

  return (
    <div className="payverse-wrap">
      <div className="payverse">
        <PayverseHeader logoOn={false} />
        <div className="b-section">
          <div
            style={{
              display: 'flex',
              minHeight: 'calc(100dvh - 200px)',
              padding: '0 20px',
              alignItems: 'center',
            }}
          >
            <div className="loading-section">
              <div className="spin"></div>
              <p>{t('pending_exp')}</p>
            </div>
          </div>
        </div>

        {isPolling && (
          <div className="c-section">
            <div className="box confirmBox">
              <button
                className="orange"
                onClick={() => transactPolling.mutate()}
              >
                {t('payment_confirm')}
              </button>
              <button
                className="gray"
                onClick={() => {
                  clearPollingTimers();
                  transactLastResult.mutate();
                }}
              >
                {t('payment_processing_close')}
              </button>
            </div>
          </div>
        )}

        <div className="d-section">
          <div className="box">
            <div className="copy">
              Copyright (c) eromnet Co., Ltd. All rights reserved.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WechatResult;
